import React, {
  useContext, useEffect, useState
} from 'react';

import { APIClient } from '../client';
import {
  AssetsContext, AssetsProvider, DebtorsContext, DebtorsProvider, ExecutorsContext, ExecutorsProvider, PetArrangementsContext, PetArrangementsProvider
} from '../context';
import { objectToQueryString } from '../hooks';
import { NewWillContext } from '../pages/new-will';

// Type definitions based on your data object
interface Person {
  id: string;
  firstName: string;
  middleName: string;
  lastName: string;
  contactEmail: string;
  contactPhone: string;
  phoneNumber: string | null;
  birthCity: string | null;
  birthState: string | null;
  birthCountry: string | null;
  birthCounty: string | null;
  dob: string | null;
  race: string | null;
  linkedUserId: string | null;
  userId: string;
  createdDatetime: string;
  modifiedDatetime: string;
}

interface Asset {
  id: string;
  willId: string;
  userId: string;
  assetType: string;
  description: string;
  name: string;
  assetValue: number | null;
  createdDatetime: string;
  modifiedDatetime: string;
  fullCount: string;
  beneficiaries: Person[];
}

interface Executor extends Person {
  person: Person;
}

interface Arrangement {
  id: string;
  willId: string;
  userId: string;
  type: string;
  name: string;
  careInstructions: string;
  careTakerId: string;
  fundValue: number | null;
  birthday: string | null;
  createdDatetime: string;
  modifiedDatetime: string;
  fullCount: string;
  careTaker: Person;
}

interface Debtor {
  // Add debtor properties as they become available
  [key: string]: any;
}

interface WillData {
  id: string;
  status: string;
  createdDatetime: string;
  modifiedDatetime: string;
  userId: string;
  personId: string;
  primaryBeneficiaryId: string;
  fullCount: string;
  debtors: Debtor[];
  assets: Asset[];
  executors: Executor[];
  arrangements: Arrangement[];
}

interface WillTemplateProps {
  willData: WillData;
  testatorName?: string;
  testatorAddress?: string;
}

export const WillTemplateComponent: React.FC<WillTemplateProps> = ({ 
  willData, 
  testatorName = '[TESTATOR NAME TO BE PROVIDED]',
  testatorAddress = '[TESTATOR ADDRESS TO BE PROVIDED]'
}) => {
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatPersonName = (person: Person): string => {
    return `${person.firstName} ${person.middleName ? person.middleName + ' ' : ''}${person.lastName}`.trim();
  };

  const getPrimaryBeneficiary = (): Person | null => {
    return willData.assets?.[0]?.beneficiaries?.[0] || {} as Person;
  };

  const primaryBeneficiary = getPrimaryBeneficiary();

  return (
    <div className="will-template" style={{ maxWidth: '8.5in', margin: '0 auto', fontFamily: 'Times, serif', lineHeight: '1.6' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: '0 0 1rem 0' }}>
          LAST WILL AND TESTAMENT
        </h1>
        <p style={{ color: 'red', fontWeight: 'bold', fontSize: '14px' }}>
          [DRAFT - FOR DEVELOPMENT/TESTING ONLY - REQUIRES LEGAL REVIEW]
        </p>
      </div>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          DECLARATION
        </h2>
        <p>
          I, <strong>{testatorName}</strong>, of <strong>{testatorAddress}</strong>, being of sound mind and disposing memory, 
          do hereby make, publish, and declare this to be my Last Will and Testament, hereby revoking all former wills and codicils made by me.
        </p>
        <div style={{ margin: '1rem 0', fontSize: '14px', color: '#666' }}>
          <p><strong>Will ID:</strong> {willData.id}</p>
          <p><strong>Created:</strong> {formatDate(willData.createdDatetime)}</p>
          <p><strong>Last Modified:</strong> {formatDate(willData.modifiedDatetime)}</p>
          <p><strong>Status:</strong> {willData.status.toUpperCase()}</p>
        </div>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE I - EXECUTORS
        </h2>
        <p>I hereby nominate and appoint the following person(s) as Executor(s) of this Will:</p>
        
        {(willData.executors || []).map((executor, index) => (
          <div key={executor.id} style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#f9f9f9' }}>
            <p><strong>{index === 0 ? 'Primary' : 'Alternate'} Executor:</strong></p>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li><strong>Name:</strong> {formatPersonName(executor)}</li>
              <li><strong>Contact:</strong> {executor.contactEmail} | {executor.contactPhone || 'No phone provided'}</li>
              {executor.birthCity && executor.birthState && (
                <li><strong>Location:</strong> {executor.birthCity}, {executor.birthState}</li>
              )}
            </ul>
          </div>
        ))}

        <p>I direct that no bond or other security shall be required of any Executor named herein.</p>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE II - DEBTS AND EXPENSES
        </h2>
        <p>I direct my Executor to pay all of my just debts, funeral expenses, and the expenses of administration of my estate.</p>
        
        <div style={{ margin: '1rem 0' }}>
          <strong>Outstanding Debts:</strong>
          {(willData.debtors || []).length > 0 ? (
            <ul>
              {willData.debtors.map((debtor, index) => (
                <li key={index}>
                  {/* Add debtor details when structure is defined */}
                  Debtor information: {JSON.stringify(debtor)}
                </li>
              ))}
            </ul>
          ) : (
            <p style={{ fontStyle: 'italic', marginLeft: '1rem' }}>
              No specific debts have been documented in this will.
            </p>
          )}
        </div>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE III - DISPOSITION OF ASSETS
        </h2>
        <p>I give, devise, and bequeath my assets as follows:</p>

        {(willData.assets || []).map((asset) => (
          <div key={asset.id} style={{ margin: '1.5rem 0', padding: '1rem', border: '1px solid #ddd' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {asset.name}
            </h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li><strong>Type:</strong> {asset.assetType.replace('_', ' ').toUpperCase()}</li>
              <li><strong>Description:</strong> {asset.description}</li>
              <li><strong>Estimated Value:</strong> {asset.assetValue ? `$${asset.assetValue.toLocaleString()}` : 'Not specified'}</li>
            </ul>

            <div style={{ marginTop: '1rem' }}>
              <strong>Beneficiaries for this asset:</strong>
              {asset.beneficiaries.map((beneficiary) => (
                <div key={beneficiary.id} style={{ margin: '0.5rem 0 0 1rem', padding: '0.5rem', backgroundColor: '#f0f8ff' }}>
                  <p><strong>{formatPersonName(beneficiary)}</strong></p>
                  <ul style={{ listStyle: 'none', padding: 0, fontSize: '14px' }}>
                    <li>Email: {beneficiary.contactEmail}</li>
                    <li>Phone: {beneficiary.contactPhone || 'Not provided'}</li>
                    {beneficiary.dob && beneficiary.birthCity && beneficiary.birthState && (
                      <li>Born: {formatDate(beneficiary.dob)} in {beneficiary.birthCity}, {beneficiary.birthState}</li>
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE IV - PET CARE ARRANGEMENTS
        </h2>
        <p>I make the following arrangements for the care of my beloved pets:</p>

        {(willData.arrangements || []).map((arrangement) => (
          <div key={arrangement.id} style={{ margin: '1.5rem 0', padding: '1rem', border: '1px solid #ddd', backgroundColor: '#fffacd' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {arrangement.name} ({arrangement.type})
            </h3>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              <li><strong>Pet Type:</strong> {arrangement.type.charAt(0).toUpperCase() + arrangement.type.slice(1)}</li>
              <li><strong>Care Instructions:</strong> {arrangement.careInstructions}</li>
              <li><strong>Designated Caretaker:</strong> {formatPersonName(arrangement.careTaker)}</li>
              <li style={{ marginLeft: '1rem' }}>Contact: {arrangement.careTaker.contactEmail} | {arrangement.careTaker.contactPhone || 'No phone provided'}</li>
              <li><strong>Care Fund:</strong> {arrangement.fundValue ? `$${arrangement.fundValue.toLocaleString()}` : 'Not specified'}</li>
              <li><strong>Birthday:</strong> {arrangement.birthday ? formatDate(arrangement.birthday) : 'Not specified'}</li>
            </ul>
          </div>
        ))}

        <p style={{ fontStyle: 'italic', marginTop: '1rem' }}>
          I direct that my pets shall be cared for according to these specific instructions, and I request that the designated caretakers provide loving homes for these animals.
        </p>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE V - RESIDUARY CLAUSE
        </h2>
        <p>All the rest, residue, and remainder of my estate, both real and personal, of whatsoever kind and wheresoever situated, I give, devise, and bequeath to:</p>
        
        {primaryBeneficiary ? (
          <div style={{ margin: '1rem 0', padding: '1rem', backgroundColor: '#f0f8ff' }}>
            <p><strong>Primary Beneficiary:</strong> {formatPersonName(primaryBeneficiary)}</p>
            <p>Contact: {primaryBeneficiary.contactEmail} | {primaryBeneficiary.contactPhone || 'No phone provided'}</p>
          </div>
        ) : (
          <p style={{ color: 'red', fontStyle: 'italic' }}>[PRIMARY BENEFICIARY NOT FOUND]</p>
        )}
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE VI - SURVIVORSHIP
        </h2>
        <p>
          If any beneficiary named herein shall predecease me or shall die within thirty (30) days after my death, 
          then such beneficiary's share shall pass to the remaining beneficiaries in equal shares.
        </p>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          ARTICLE VII - POWERS OF EXECUTOR
        </h2>
        <p>I grant to my Executor full power and authority to:</p>
        <ol>
          <li>Sell, transfer, or dispose of any property of my estate</li>
          <li>Invest and reinvest estate funds</li>
          <li>Distribute assets in kind or in cash</li>
          <li>Settle claims and pay debts</li>
          <li>Take all actions necessary for the proper administration of my estate</li>
        </ol>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          EXECUTION
        </h2>
        <p>IN WITNESS WHEREOF, I have hereunto set my hand this _____ day of _____________, 20__.</p>
        
        <div style={{ margin: '2rem 0' }}>
          <p><strong>TESTATOR:</strong></p>
          <div style={{ margin: '2rem 0' }}>
            <div style={{ borderBottom: '1px solid black', marginBottom: '0.5rem' }}>&nbsp;</div>
            <p>{testatorName}</p>
          </div>
        </div>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          WITNESS ATTESTATION
        </h2>
        <p>
          We, the undersigned, certify that the testator signed this Will in our presence, that we witnessed the signing, 
          and that the testator appeared to be of sound mind and under no duress.
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', margin: '2rem 0' }}>
          <div style={{ width: '45%' }}>
            <p><strong>WITNESS 1:</strong></p>
            <div style={{ margin: '1rem 0' }}>
              <p>Name: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Address: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Signature: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Date: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
            </div>
          </div>
          <div style={{ width: '45%' }}>
            <p><strong>WITNESS 2:</strong></p>
            <div style={{ margin: '1rem 0' }}>
              <p>Name: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Address: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Signature: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
              <p>Date: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '100%' }}>&nbsp;</span></p>
            </div>
          </div>
        </div>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '1rem' }}>
          NOTARIZATION
        </h2>
        <div style={{ margin: '1rem 0' }}>
          <p>State of: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '200px' }}>&nbsp;</span></p>
          <p>County of: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '200px' }}>&nbsp;</span></p>
        </div>

        <p style={{ margin: '1rem 0' }}>
          On this _____ day of _____________, 20__, before me personally appeared {testatorName}, who proved to me on the basis of satisfactory evidence to be the person whose name is subscribed to the within instrument and acknowledged to me that he/she executed the same in his/her authorized capacity, and that by his/her signature on the instrument the person, or the entity upon behalf of which the person acted, executed the instrument.
        </p>

        <div style={{ margin: '2rem 0' }}>
          <div style={{ borderBottom: '1px solid black', marginBottom: '0.5rem' }}>&nbsp;</div>
          <p>Notary Public Signature</p>
          <p style={{ marginTop: '1rem' }}>My commission expires: <span style={{ borderBottom: '1px solid black', display: 'inline-block', width: '150px' }}>&nbsp;</span></p>
        </div>
      </section>

      <hr style={{ margin: '2rem 0' }} />

      <div style={{ 
        backgroundColor: '#ffe4e1', 
        padding: '1rem', 
        border: '2px solid #ff6b6b', 
        borderRadius: '4px',
        textAlign: 'center'
      }}>
        <p style={{ fontWeight: 'bold', color: '#d63031' }}>
          IMPORTANT LEGAL DISCLAIMER:
        </p>
        <p style={{ fontSize: '14px', color: '#2d3436' }}>
          This template is for development and testing purposes only. It has not been reviewed by legal counsel and should not be used as an actual legal document. 
          State laws vary significantly regarding will requirements, and proper legal advice should be sought before creating any legally binding will.
        </p>
      </div>
    </div>
  );
};

export default WillTemplate;

export function WillTemplate() {
  return (
    <AssetsProvider>
      <PetArrangementsProvider>
        <DebtorsProvider>
          <ExecutorsProvider>
            <WillDataProvider />
          </ExecutorsProvider>
        </DebtorsProvider>
      </PetArrangementsProvider>
    </AssetsProvider>
  );
}
  
const getPerson = async (personId) => {
  const client = new APIClient('api/people');
  const person = await client.show(personId);
  return person;
};
  
function WillDataProvider() {
  const { will } = useContext(NewWillContext);
  const { assets } = useContext(AssetsContext);
  const { arrangements } = useContext(PetArrangementsContext);
  const { debtors } = useContext(DebtorsContext);
  const { executors } = useContext(ExecutorsContext);
  console.log('TEMPLATE ASSETS', assets);
  console.log('TEMPLATE PET ARRANGEMENTS', arrangements);
  console.log('TEMPLATE DEBTORS', debtors);
  console.log('TEMPLATE EXECUTORS', executors);
  
  const [ willData, setWillData ] = useState(will);
  
  useEffect(() => {
    const getAllBeneficiaries = async () => {
      const assetBequests = await Promise.all(assets.map((asset) => {
        return getBequests(asset);
      }));
  
      console.log('all bequests', assetBequests);
  
      const people = await Promise.all(assetBequests.map(async (bequests) => {
        console.log('bequests', bequests);
        return await Promise.all(bequests.map(async (bequest) => {
          return getPerson(bequest.beneficiaryId);
        }));
      }));
  
      console.log('People', people);
      return people;
    };
  
    const getBequests = async (asset) => {
      const queryString = objectToQueryString({ asset_id: asset.id });
      const client = new APIClient(`api/bequests?${queryString}`);
  
      const bequests = await client.index();
  
      console.log('Bequests', bequests);
  
      return bequests;
    };
  
    const setBeneficiaries = async () => {
      const beneficiaries = await getAllBeneficiaries();
      const newWillData = {
        ...willData,
        assets: assets.map((asset, idx) => {
          return {
            ...asset,
            beneficiaries: beneficiaries[idx]
          };
        })
      };
  
      setWillData(newWillData);
    };
  
    if (!willData.assets) {
      setBeneficiaries();
    } 
  }, [ assets, willData ]);
  
  useEffect(() => {
    const getCareTakers = async () => {
      const result = await Promise.all(arrangements.map((arrangement) => {
        return getPerson(arrangement.careTakerId);
      }));
  
      console.log('Pet Care Takers', result);
      return result;
    };
  
    const setCareTakers = async () => {
      const careTakers = await getCareTakers();
      const newWillData = {
        ...willData,
        arrangements: arrangements.map((arrangement, idx) => {
          return { ...arrangement, careTaker: careTakers[idx] };
        })
      };
  
      setWillData(newWillData);
    };
  
    if (!willData.arrangements) {
      setCareTakers();
    }
  }, [ arrangements, willData ]);
  
  useEffect(() => {
    const getDebtors = async () => {
      const result = await Promise.all(debtors.map((debtor) => {
        return getPerson(debtor.personId);
      }));
  
      console.log('Debtors', result);
      return result;
    };
  
    const setDebtors = async () => {
      const debtors = await getDebtors();
      const newWillData = {
        ...willData,
        debtors: debtors.map((debtor, idx) => {
          return { ...debtor, person: debtors[idx] };
        })
      };
  
      setWillData(newWillData);
    };
  
    if (!willData.debtors) {
      setDebtors();
    }
  }, [ debtors, willData ]);
  
  useEffect(() => {
    const getExecutors = async () => {
      const result = await Promise.all(executors.map((executor) => {
        return getPerson(executor.personId);
      }));
  
      console.log('Executors', result);
      return result;
    };
  
    const setExecutors = async () => {
      const executors = await getExecutors();
      const newWillData = {
        ...willData,
        executors: executors.map((executor, idx) => {
          return { ...executor, person: executors[idx] };
        })
      };
  
      setWillData(newWillData);
    };
  
    if (!willData.executors) {
      setExecutors();
    }
  }, [ executors, willData ]);
  
  console.log('TEMPLATE WILL DATA', willData);
    
  return (
    <WillTemplateComponent willData={willData} />
  );
}
  